<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'

import { useConfirmDialog } from '@/composables/useConfirmDialog'
import { useSSOAccounts } from '@/composables/useSSOAccounts'

import { notifyError, notifySuccess } from '@/utils/ui/handleNotification'

import * as aggr from '@/api-generator/aggr'
import { feUserProfileAPI } from '@/APIs'
import googleIcon from '@/assets/image/register/gologo.png'
import lineIcon from '@/assets/image/register/linelogo-colorful.png'

import type { OauthProvider } from '@/types/user'

const { handleGetSSOAccounts, isSSOAccountLoading, isSSOAccountBinding, ssoAccounts } = useSSOAccounts()

interface OauthList {
  name: string
  provider: OauthProvider
  icon: string
  isBinding: boolean
  providerId: string
}

const oauthList = ref<OauthList[]>([
  {
    name: 'LINE',
    provider: 'line',
    icon: lineIcon,
    isBinding: false,
    providerId: ''
  },
  {
    name: 'GOOGLE',
    provider: 'google',
    icon: googleIcon,
    isBinding: false,
    providerId: ''
  }
])

const updateOauthListWithBindings = (ssoAccounts: aggr.UserProfileGetCurrentUserBindSsoAccountsRs[]) => {
  oauthList.value = oauthList.value.map((oauth) => {
    const providerMapping = {
      line: aggr.AuthProvider.Line,
      google: aggr.AuthProvider.Google
    }

    const account = ssoAccounts.find((account) => account.provider === providerMapping[oauth.provider])

    return {
      ...oauth,
      isBinding: !!account,
      providerId: account ? account.providerId || '' : ''
    }
  })
}

const openBindingWindow = (provider: OauthProvider) => {
  const baseUrl = `${window.location.origin}/sso-binding`
  const params = new URLSearchParams({ provider })

  const ssoCallbackUrl = `${baseUrl}?${params.toString()}`

  // 設定視窗的寬度和高度
  const width = 600
  const height = 500

  // 計算視窗的垂直和水平位置，使其顯示在螢幕正中間
  const left = (window.innerWidth - width) / 2 + window.screenX
  const top = (window.innerHeight - height) / 2 + window.screenY

  window.open(ssoCallbackUrl, '第三方帳號綁定', `width=${width},height=${height},top=${top},left=${left}`)
}

const getAuthProviderCode = (provider: string) => {
  // 將 provider 的第一個字母轉為大寫
  const formattedProvider = provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()
  const providerCode = aggr.AuthProvider[formattedProvider as keyof typeof aggr.AuthProvider]
  return providerCode
}

// 解除綁定第三方帳號
const isSSOAccountUnBinding = ref(false)
const handleRemoveSsoAccount = async (providerId: string, provider: aggr.AuthProvider) => {
  isSSOAccountUnBinding.value = true
  const reqBody: aggr.UserProfileDeleteCurrentUserBindSsoAccountRq = {
    providerId,
    provider
  }
  try {
    await feUserProfileAPI.removeUserSSOAccount(reqBody)
    await handleGetSSOAccounts()
    updateOauthListWithBindings(ssoAccounts.value)
    notifySuccess('解除綁定成功')
  } catch (error: any) {
    console.error(error)
    const errorMsg = error.response && error.response.data.message
    notifyError(errorMsg || '解除綁定失敗')
  } finally {
    isSSOAccountUnBinding.value = false
  }
}

const openConfirmSSOAccountUnBindingDialog = (
  providerId: string,
  provider: aggr.AuthProvider,
  providerName: string
) => {
  const { confirm } = useConfirmDialog()
  confirm({
    title: '是否解除綁定',
    text: `是否要取消綁定 ${providerName} 帳號`,
    confirmButtonText: '確定',
    onConfirm: async () => {
      await handleRemoveSsoAccount(providerId, provider)
    }
  })
}

watch(
  ssoAccounts,
  (newVal) => {
    updateOauthListWithBindings(newVal)
  },
  {
    deep: true
  }
)

onMounted(async () => {
  await handleGetSSOAccounts()
})
</script>

<template>
  <div class="profile-binding-oauth-account">
    <div class="profile-binding-oauth-account-header">
      <h3 class="profile-step-title">第三方帳號管理</h3>
      <p class="profile-binding-oauth-account-description">綁定後可使用第三方帳號登入</p>
    </div>
    <ul v-loading="isSSOAccountLoading || isSSOAccountBinding" class="binding-oauth-list">
      <template v-if="!isSSOAccountLoading">
        <li v-for="item in oauthList" :key="item.provider" class="binding-oauth-list-item">
          <div class="binding-oauth-list-item-icon">
            <img :src="item.icon" :alt="item.provider" class="binding-oauth-logo" />
            <el-tag v-if="!item.isBinding" type="info">未綁定</el-tag>
            <el-tag v-else type="success">已綁定</el-tag>
          </div>
          <el-button v-if="!item.isBinding" plain @click="openBindingWindow(item.provider)">進行綁定</el-button>
          <el-button
            v-else
            type="danger"
            plain
            :loading="isSSOAccountUnBinding"
            @click="
              openConfirmSSOAccountUnBindingDialog(item.providerId, getAuthProviderCode(item.provider), item.name)
            "
            >解除綁定</el-button
          >
        </li>
      </template>
    </ul>
  </div>
</template>
<style lang="scss" scoped>
.profile-binding-oauth-account {
  padding-top: 23px;
  &-header {
    margin-bottom: 48px;
  }
  &-description {
    margin-top: 12px;
    display: block;
    color: var(--gray-7d);
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 1.147px;
  }
  .binding-oauth-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    // padding-top: 48px;
    width: 40%;
    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
      &-icon {
        display: flex;
        align-items: center;
        gap: 20px;
      }
    }
  }
  .binding-oauth-logo {
    width: 36px;
    height: 36px;
  }
  .loading-container {
    height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
